import { makeAutoObservable, runInAction } from 'mobx';

import { cmApi } from '@src/api';
import type { CmApi, ControlGroupDto } from '@src/api';

import { controlGroupsStore } from '../controlGroupsStore';

class ControlGroupEditStore {
  controlGroup: ControlGroupDto | null;

  isNew = false;

  editMode = false;

  isLoading = false;

  api: CmApi;

  hiddenEdit = false;

  hiddenDelete = false;

  hiddenClean = false;

  hiddenSave = false;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    this.controlGroup = null;

    makeAutoObservable(this);
  }

  get isEditMode() {
    return this.editMode || this.isNew;
  }

  setNew = (isNew = true) => {
    this.isNew = isNew;
    this.controlGroup = null;
  };

  setEditMode = (editMode = true) => {
    this.editMode = editMode;
  };

  save = async (controlGroup: ControlGroupDto) => {
    const { data } = await (this.isNew
      ? this.api.controlGroups.addControlGroup(controlGroup)
      : this.api.controlGroups.updateControlGroupByCode(controlGroup));

    if (data) {
      controlGroupsStore.setIsChanged(true);

      if (this.isNew) {
        return { ctrlGroupCode: data.ctrlGroupCode };
      }
      runInAction(() => {
        this.controlGroup = data;
        this.setEditMode(false);
      });
    }
    return undefined;
  };

  delete = async () => {
    if (this.controlGroup) {
      await this.api.controlGroups.deleteControlGroupByCode(this.controlGroup.ctrlGroupCode ?? '');
      controlGroupsStore.setIsChanged(true);
    }
  };

  fetch = async (ctrlGroupCode?: string): Promise<void> => {
    if (!ctrlGroupCode) {
      return;
    }
    runInAction(() => {
      this.isLoading = true;
      this.setEditMode(false);
      this.setNew(false);
    });

    const { data } = await this.api.controlGroups.getControlGroupByCode(ctrlGroupCode);

    runInAction(() => {
      this.controlGroup = data;
      this.isLoading = false;
    });
  };

  setHiddenEdit = (hiddenEdit = true): void => {
    this.hiddenEdit = hiddenEdit;
  };

  setHiddenDelete = (hiddenDelete = true): void => {
    this.hiddenDelete = hiddenDelete;
  };

  setHiddenClean = (hiddenClean = true): void => {
    this.hiddenClean = hiddenClean;
  };

  setHiddenSave = (hiddenSave = true): void => {
    this.hiddenSave = hiddenSave;
  };
}

export const controlGroupEditStore = new ControlGroupEditStore({ api: cmApi });
